import * as THREE from 'three';

export type AssetType = 'equipment' | 'personnel';
export type AssetStatus = 'active' | 'idle' | 'alert' | 'offline';

export const STATUS_COLORS: Record<AssetStatus, string> = {
  active: '#10B981',
  idle: '#F59E0B',
  alert: '#EF4444',
  offline: '#6B7280',
};

// Equipment subtype accent colors — cool tones for contrast against amber mine map
export const SUBTYPE_COLORS: Record<string, string> = {
  truck: '#818CF8',          // indigo-400 — cool blue-violet
  rescue_vehicle: '#EC4899', // pink-500 — vivid magenta
  loader: '#7C3AED',         // violet-600 — deep purple
  drill_rig: '#06B6D4',      // cyan-500 — cool teal
};

// Personnel subtype accent colors — cool tones for contrast
export const PERSONNEL_COLORS: Record<string, string> = {
  worker: '#06B6D4',         // cyan-500
  supervisor: '#A78BFA',     // violet-400
  emergency_team: '#EC4899', // pink-500
};

export interface RTLSMarkerData {
  id: string;
  name: string;
  /** Human-readable identifier — equipment.asset_id or personnel.employee_id.
   *  When present, used in place of `name` for in-scene labels. */
  externalId?: string;
  type: AssetType;
  subType?: string;
  status: AssetStatus;
  position: {x: number; y: number; z: number};
  label?: string;
  level?: number;
}

// Extract initials from name: "Nina R" → "NR", "Dev C" → "DC"
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (name[0] || '?').toUpperCase();
}

// Draw equipment type-specific icon inside the marker circle
function drawEquipmentIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  subType?: string,
) {
  ctx.fillStyle = '#E6E6E6';
  ctx.strokeStyle = '#E6E6E6';
  ctx.lineWidth = radius * 0.08;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const s = radius * 0.55;

  switch (subType) {
    case 'truck': {
      const w = s * 1.4, h = s * 0.8;
      ctx.beginPath();
      ctx.roundRect(cx - w / 2, cy - h / 2, w * 0.6, h, 3);
      ctx.fill();
      ctx.beginPath();
      ctx.roundRect(cx - w / 2 + w * 0.65, cy - h * 0.3, w * 0.35, h * 0.65, 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.beginPath();
      ctx.arc(cx - w * 0.25, cy + h / 2 + 1, s * 0.12, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx + w * 0.15, cy + h / 2 + 1, s * 0.12, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'loader': {
      const bw = s * 1.0, bh = s * 0.7;
      ctx.beginPath();
      ctx.roundRect(cx - bw / 2, cy - bh / 4, bw, bh, 3);
      ctx.fill();
      ctx.lineWidth = s * 0.12;
      ctx.strokeStyle = '#E6E6E6';
      ctx.beginPath();
      ctx.moveTo(cx + bw / 2, cy);
      ctx.lineTo(cx + bw / 2 + s * 0.35, cy - s * 0.4);
      ctx.lineTo(cx + bw / 2 + s * 0.45, cy - s * 0.15);
      ctx.stroke();
      break;
    }
    case 'drill_rig': {
      ctx.beginPath();
      ctx.roundRect(cx - s * 0.4, cy + s * 0.1, s * 0.8, s * 0.35, 2);
      ctx.fill();
      ctx.lineWidth = s * 0.12;
      ctx.beginPath();
      ctx.moveTo(cx, cy + s * 0.1);
      ctx.lineTo(cx, cy - s * 0.6);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx - s * 0.1, cy - s * 0.6);
      ctx.lineTo(cx, cy - s * 0.8);
      ctx.lineTo(cx + s * 0.1, cy - s * 0.6);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'rescue_vehicle': {
      const arm = s * 0.55, thick = s * 0.25;
      ctx.beginPath();
      ctx.roundRect(cx - arm, cy - thick / 2, arm * 2, thick, 2);
      ctx.fill();
      ctx.beginPath();
      ctx.roundRect(cx - thick / 2, cy - arm, thick, arm * 2, 2);
      ctx.fill();
      break;
    }
    default: {
      ctx.font = `bold ${radius * 0.7}px -apple-system, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('⚙', cx, cy);
      break;
    }
  }
}

export function createMarkerTexture(
  type: AssetType,
  _status: AssetStatus,
  size: number = 64,
  subType?: string,
  name?: string,
): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.42;
  const borderWidth = size * 0.035;

  // Clear
  ctx.clearRect(0, 0, size, size);

  // Shadow
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 2;

  // Neutral gray border ring
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = '#555555';
  ctx.fill();
  ctx.restore();

  // Main circle (inner) — solid #333333
  ctx.beginPath();
  ctx.arc(cx, cy, radius - borderWidth, 0, Math.PI * 2);
  ctx.fillStyle = '#333333';
  ctx.fill();

  // Draw content based on type
  if (type === 'personnel') {
    // Personnel: draw initials text
    const initials = name ? getInitials(name) : '?';
    ctx.fillStyle = '#E6E6E6';
    ctx.font = `bold ${radius * 0.75}px -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, cx, cy + 1);
  } else {
    // Equipment: draw type-specific icon
    drawEquipmentIcon(ctx, cx, cy, radius, subType);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

// Cache textures to avoid recreating them
const textureCache = new Map<string, THREE.Texture>();

export function getCachedMarkerTexture(
  type: AssetType,
  status: AssetStatus,
  subType?: string,
  name?: string,
): THREE.Texture {
  // Personnel textures are per-name (unique initials), equipment per-subtype
  const nameKey = type === 'personnel' && name ? name : 'default';
  const key = `${type}_${status}_${subType || 'default'}_${nameKey}`;
  if (!textureCache.has(key)) {
    textureCache.set(key, createMarkerTexture(type, status, 256, subType, name));
  }
  return textureCache.get(key)!;
}

export function clearTextureCache(): void {
  textureCache.forEach(texture => texture.dispose());
  textureCache.clear();
}
